import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  ChevronLeft,
  ShoppingCart,
  CheckCircle,
  Plus,
  Trash2,
  X,
  Loader2,
  Search,
  Navigation,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { SelectedProduct } from "../../../../../services/api/OrderService/types";
import { Driver } from "../../../../../services/api/LaborderService/types";
import { AdminOrderService } from "../../../../../services/api/AdminOrderService/admin.order.service";
import medicineIcon from "../../../../../assets/images/medicineIcon.png";
import defaultProduct from "../../../../../assets/images/default_product.jpg";
import OrderDetailsHeader from "./OrderDetailsHeader";
import { AdminOrderManagementService } from "../../../../../services/api/admin-order.service";
import { OrderProduct } from "../../../../../services/api/admin-order.types";

interface DispatchProductsProps {
  orderId: number;
  handleDispatchProducts: (value: boolean) => void;
  selectedProductLists: SelectedProduct[];
  setSelectedProductLists: React.Dispatch<React.SetStateAction<SelectedProduct[]>>;
  handleProcessOrder: () => void;
  drivers: Driver[];
  selectedDriver: string | undefined;
  onDriverSelect: (driver: Driver) => void;
  submitting: boolean;
  clinicId: string;
}

const DispatchProducts = ({
  orderId,
  handleDispatchProducts,
  selectedProductLists,
  setSelectedProductLists,
  handleProcessOrder,
  drivers,
  selectedDriver,
  onDriverSelect,
  submitting,
  clinicId
}: DispatchProductsProps) => {

  const [showRiderModal, setShowRiderModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [riderSearch, setRiderSearch] = useState("");
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItemsCount: 0,
    totalPages: 1,
  });
  const [orderDetails, setOrderDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  // Returns the effective product details, using substitute data when approved
  const getEffectiveProduct = (p: OrderProduct) => {
    if (p.substituteStatus === "Approved" && p.substituteProductId) {
      return {
        productId: p.substituteProductId,
        name: p.substituteProductName || p.name,
        productImage: p.substituteProductImage || p.productImage,
        quantity: p.quantity,
        productPrice: p.substitutePrice || p.productPrice,
        productMrp: p.substituteMrp || p.productMrp,
        isSubstituted: true,
        originalName: p.name,
      };
    }
    return {
      productId: p.productId,
      name: p.name,
      productImage: p.productImage,
      quantity: p.quantity,
      productPrice: p.productPrice,
      productMrp: p.productMrp,
      isSubstituted: false,
      originalName: p.name,
    };
  };

  // Collapsible States - matching PrepareOrder admin panel flow
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await AdminOrderManagementService.getOrderDetails(orderId);
      setOrderDetails(response);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  // Fetch Clinic stocks inside the modal
  const fetchStockProducts = useCallback(
    async (searchTerm = "", page = 1) => {
      if (!clinicId) return;
      try {
        setModalLoading(true);
        const response = await AdminOrderService.getClinicProductListing(
          page,
          10,
          searchTerm,
          clinicId
        );
        if (response?.data?.data && Array.isArray(response.data.data)) {
          const transformed = response.data.data.map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            product_name: item.product?.name || "Unknown Product",
            manufacturer_name: item.manufacturer?.manufacturer_name || "Unknown Manufacturer",
            product_image: item.product?.productImage || medicineIcon,
            landing_cost: parseFloat(item.landing_cost) || 0,
            mrp: parseFloat(item.productMrp || item.mrp),
            quantity: item.remaining_stocks || 0,
            expiry_date: item.expiry_date,
            batch_number: item.batch_number,
            hsn_number: item.hsn_number,
            product_rate: parseFloat(item.product_rate) || 0,
            discount: parseFloat(item.sellingDiscount) || 0,
            cgst: parseFloat(item.cgst) || 0,
            sgst: parseFloat(item.sgst) || 0,
            free_items: item.free_items || 0,
            finalSP: parseFloat(item.finalSP) || 0,
            sellingDiscount: parseFloat(item.sellingDiscount) || 0,
            remainingStocks: item.remaining_stocks,
            totalStock: item.remaining_stocks,
            noOfItemsInPack: item?.product?.tablet_per_stripe || 10,
            perPackPrice: parseFloat(item.productMrp || item.mrp),
            expiry: item.expiry_date,
            batch: item.batch_number,
            salt: item.salt,
            product: item.product,
          }));
          setAvailableProducts(transformed);
          setPagination({
            currentPage: response.data.pagination?.currentPage || 1,
            itemsPerPage: 10,
            totalItemsCount: response.data.pagination?.totalItemsCount || 0,
            totalPages: response.data.pagination?.totalPages || 1,
          });
        }
      } catch (err) {
        toast.error("Failed to load clinic stock medicines");
      } finally {
        setModalLoading(false);
      }
    },
    [clinicId]
  );

  // Debounced search inside the modal
  useEffect(() => {
    if (!showProductModal) return;
    const timer = setTimeout(() => {
      fetchStockProducts(searchText, 1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText, showProductModal, fetchStockProducts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={32} className="animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="p-5">
        <button onClick={() => handleDispatchProducts(false)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer mb-4 hover:text-slate-700">
          <ChevronLeft size={16} /> Back to Orders
        </button>
        <p className="text-sm text-red-500">Failed to load order details.</p>
      </div>
    );
  }

  const activeDriver = drivers.find((d) => d.id === selectedDriver);

  // Filter riders based on search
  const filteredDrivers = drivers.filter((driver) =>
    driver.name.toLowerCase().includes(riderSearch.toLowerCase())
  );

  const handleAddProduct = (product: any) => {
    // Only allow adding products that are in the patient's order (using effective product IDs)
    const isOrderedProduct = orderDetails?.products?.some(
      (p: OrderProduct) => {
        const effective = getEffectiveProduct(p);
        return effective.productId.toString() === product.product_id;
      }
    );
    if (!isOrderedProduct) {
      toast.error("Product not in patient's order list");
      return;
    }

    const existing = selectedProductLists.find((p) => p.id === product.id);
    const orderedProduct = orderDetails?.products?.find((p: OrderProduct) => {
      const effective = getEffectiveProduct(p);
      return effective.productId.toString() === product.product_id;
    });
    const requiredQty = orderedProduct?.quantity || 1;
    const currentQty = existing ? existing.selectedQuantity : 0;

    if (currentQty >= product.remainingStocks) {
      toast.error(`Stock unavailable! Only ${product.remainingStocks} items left in inventory.`);
      return;
    }

    if (currentQty >= requiredQty) {
      toast.error(`Required limit reached! The patient only ordered ${requiredQty} of this item.`);
      return;
    }

    setSelectedProductLists((prev) => {
      if (existing) {
        return prev.map((p) =>
          p.id === product.id
            ? { ...p, selectedQuantity: p.selectedQuantity + 1 }
            : p
        );
      } else {
        return [...prev, { ...product, selectedQuantity: 1 }];
      }
    });
  };

  const handleUpdateQuantity = (id: string, qty: number) => {
    const product = selectedProductLists.find((p) => p.id === id);
    if (!product) return;

    if (qty > product.remainingStocks) {
      toast.error(`Stock unavailable! Only ${product.remainingStocks} items left in inventory.`);
      return;
    }

    const requiredQty = orderDetails?.products?.find((p: OrderProduct) => {
      const effective = getEffectiveProduct(p);
      return effective.productId.toString() === product.product_id;
    })?.quantity || 1;
    if (qty > requiredQty) {
      toast.error(`Required limit reached! The patient only ordered ${requiredQty} of this item.`);
      return;
    }

    setSelectedProductLists((prev) =>
      prev.map((p) => (p.id === id ? { ...p, selectedQuantity: qty } : p))
    );
  };

  const handleRemoveProduct = (id: string) => {
    setSelectedProductLists((prev) => prev.filter((p) => p.id !== id));
  };

  const checkItemsAdded = (items: SelectedProduct[]) => {
    if (!orderDetails?.products) return false;

    const orderedProducts = orderDetails.products;
    const requiredMap: Record<string, number> = {};
    orderedProducts.forEach((order: OrderProduct) => {
      const effective = getEffectiveProduct(order);
      requiredMap[effective.productId.toString()] = effective.quantity;
    });

    const selectedMap: Record<string, number> = {};
    items.forEach((item) => {
      const productId = item.product_id;
      if (!(productId in requiredMap)) return;
      selectedMap[productId] = (selectedMap[productId] || 0) + item.selectedQuantity;
    });

    for (const order of orderedProducts) {
      const effective = getEffectiveProduct(order);
      const requiredQty = effective.quantity;
      const selectedQty = selectedMap[effective.productId.toString()] || 0;
      if (selectedQty !== requiredQty) {
        return false;
      }
    }

    return true;
  };

  const getMatchedQty = (effectiveProductId: string) => {
    const matched = selectedProductLists.filter(p => p.product_id === effectiveProductId);
    return matched.reduce((sum, item) => sum + item.selectedQuantity, 0);
  };

  const canBeProcessed = selectedDriver && checkItemsAdded(selectedProductLists);

  return (
    <div className="min-h-full flex flex-col font-sans">
      {/* Top Banner Navigation */}
      <div className="flex items-center ">
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleDispatchProducts(false)}
            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors border-none cursor-pointer"
          >
            <ChevronLeft size={16} />
          </button>
          <div>
            <h2 className="text-sm font-bold text-slate-800">Dispatch Products</h2>
            <p className="text-[0.65rem] text-slate-400 mt-0.5">Prepare medicines and assign riders matching the order specifications</p>
          </div>
        </div>
      </div>

      {/* Main 3-Column Workspace */}
      <div className="flex-grow overflow-y-auto hiddenScrollbar py-5 min-h-0">
        <div className="flex gap-5">

          {/* Left Column: Details, Rider, Products */}
          <div className="flex-1 min-w-0 flex flex-col gap-4">

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 shadow-sm">
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                onClick={() => setShowOrderDetails(!showOrderDetails)}
              >
                <span className="uppercase tracking-wider">Order Details ({orderDetails.orderNumber})</span>
                {showOrderDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showOrderDetails && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <OrderDetailsHeader order={orderDetails} />
                </div>
              )}
            </div>

            {/* Courier Rider Assignment Card */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/30">
                <div className="flex items-center gap-2">
                  <Navigation size={14} className="text-slate-400" />
                  <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Rider Assignment</span>
                </div>
                {activeDriver && (
                  <button
                    onClick={() => setShowRiderModal(true)}
                    className="text-[10px] font-bold text-emerald-600 border-none bg-transparent cursor-pointer hover:underline"
                  >
                    Change Rider
                  </button>
                )}
              </div>
              <div className="p-4">

                {activeDriver ? (
                  <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <img src={activeDriver.profilePicture || "https://via.placeholder.com/40"} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200 bg-white" />
                      <div>
                        <div className="text-xs font-bold text-slate-800">{activeDriver.name}</div>
                        <div className="text-[10px] text-slate-500 font-semibold mt-0.5">📞 {activeDriver.mobileNumber}</div>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-[9px] font-bold text-amber-700 border border-amber-100">
                      {activeDriver.pendingOrdersCount} active orders
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between py-3 text-center border-2 border-dashed border-slate-200 rounded-xl px-4 bg-slate-50/20">
                    <div className="flex items-center gap-2">
                      <Navigation className="text-slate-300" size={16} />
                      <p className="text-[10px] font-bold text-slate-400">
                        No rider assigned yet
                      </p>
                    </div>
                    <button
                      onClick={() => setShowRiderModal(true)}
                      className="px-3 py-1.5 text-[10px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-all border-none cursor-pointer shadow-sm"
                    >
                      Assign Rider
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Clinic Dispatched Products Table */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col min-h-[420px]">

              <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
                <div>
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Clinic Dispatched Products</h3>
                  <p className="text-[0.62rem] text-slate-400 mt-0.5">Add matching items from local inventory stock</p>
                </div>
                <button
                  onClick={() => setShowProductModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-xs font-bold text-emerald-600 transition-all border-none cursor-pointer shadow-sm"
                >
                  <Plus size={13} />
                  Add Medicine
                </button>
              </div>
              {selectedProductLists.length > 0 ? (
                <div className="border border-slate-200 rounded-xl overflow-hidden bg-white flex-grow">
                  <div className="grid grid-cols-[2.5fr_1.2fr_1.2fr_1fr_1.2fr_1fr_0.6fr] gap-2 px-4 py-2.5 bg-slate-50 text-[10px] font-bold text-slate-400 border-b border-slate-200 uppercase">
                    <div>Product Name</div>
                    <div>Batch No</div>
                    <div>Expiry</div>
                    <div className="text-center">Pack Qty</div>
                    <div>MRP</div>
                    <div className="text-center">Strips</div>
                    <div className="text-center">Delete</div>
                  </div>
                  <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto">
                    {selectedProductLists.map((item) => (
                      <div key={item.id} className="grid grid-cols-[2.5fr_1.2fr_1.2fr_1fr_1.2fr_1fr_0.6fr] gap-2 items-center px-4 py-3.5 text-xs font-semibold text-slate-700 hover:bg-slate-50/50 transition-colors">
                        <div className="min-w-0 pr-2">
                          <div className="truncate font-bold text-slate-800" title={item.product_name}>{item.product_name}</div>
                          <div className="text-[9px] text-slate-400 truncate mt-0.5">{item.manufacturer_name}</div>
                        </div>
                        <div className="truncate text-slate-500 font-bold">{item.batch_number || "—"}</div>
                        <div className="text-slate-500 font-semibold">{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "N/A"}</div>
                        <div className="text-center text-slate-500 font-semibold">{item.noOfItemsInPack}</div>
                        <div className="text-slate-600 font-bold">₹ {item.mrp?.toFixed(2)}</div>

                        {/* Strips Counter Input */}
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => handleUpdateQuantity(item.id, Math.max(1, item.selectedQuantity - 1))}
                            className="w-5 h-5 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 flex items-center justify-center font-bold text-slate-600 cursor-pointer border-none"
                          >
                            -
                          </button>
                          <span className="w-6 text-center text-xs font-extrabold text-slate-800">{item.selectedQuantity}</span>
                          <button
                            onClick={() => handleUpdateQuantity(item.id, item.selectedQuantity + 1)}
                            className="w-5 h-5 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 flex items-center justify-center font-bold text-slate-600 cursor-pointer border-none"
                          >
                            +
                          </button>
                        </div>

                        {/* Remove Action */}
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => handleRemoveProduct(item.id)}
                            className="w-7 h-7 rounded-md bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center border-none cursor-pointer transition-colors"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center py-16 text-slate-400 text-xs italic bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                    <ShoppingCart size={20} className="text-slate-350" />
                  </div>
                  No local medicines selected yet. Click "Add Medicine" to begin mapping stock.
                </div>)}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-[280px] flex-shrink-0 hidden lg:block">

            {/* Checklist Validation & Status summary */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm sticky top-5 flex flex-col gap-4">
              <div>
                <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Required Inventory Checklist</h4>
              </div>

              <div className="space-y-2.5 max-h-[160px] overflow-y-auto pr-1">
                {orderDetails?.products?.map((p: OrderProduct) => {
                  const effective = getEffectiveProduct(p);
                  const matchedQty = getMatchedQty(effective.productId.toString());
                  const isMatched = matchedQty === effective.quantity;

                  return (
                    <div key={p.productItemId} className="flex justify-between items-center text-xs">
                      <div className="min-w-0 pr-2">
                        <p className="font-bold text-slate-600 truncate">{effective.name}</p>
                        {effective.isSubstituted && (
                          <p className="text-[8px] text-indigo-500 font-semibold truncate">Substituted from: {effective.originalName}</p>
                        )}
                        <span className={`text-[9px] font-bold border rounded px-1 mt-0.5 inline-block ${isMatched
                          ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                          : "bg-slate-55 bg-slate-50 text-slate-500 border-slate-150"
                          }`}>
                          Required × {effective.quantity} | Mapped × {matchedQty}
                        </span>
                      </div>
                      <span className={`flex-shrink-0 font-extrabold text-[11px] ${isMatched ? "text-emerald-500" : "text-amber-500"}`}>
                        {isMatched ? "Matched" : `${matchedQty}/${effective.quantity}`}
                      </span>
                    </div>
                  );
                })}
              </div>

              <hr className="border-slate-100" />

              <div className="flex flex-col gap-2.5 text-xs font-semibold text-slate-500">
                <div className="flex justify-between">
                  <span>Order Total Value</span>
                  <span className="text-slate-800 font-extrabold">₹{parseFloat(orderDetails?.totalAmount || "0").toFixed(2)}</span>
                </div>

              </div>

              <hr className="border-slate-105 border-slate-100" />

              {/* Checklist flags visual status */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2 text-xs font-semibold">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Rider Assigned</span>
                  <span className={selectedDriver ? "text-emerald-600 font-bold" : "text-rose-500 font-bold"}>
                    {selectedDriver ? "✓ Ready" : "✗ Required"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Medicines Matched</span>
                  <span className={checkItemsAdded(selectedProductLists) ? "text-emerald-600 font-bold" : "text-rose-500 font-bold"}>
                    {checkItemsAdded(selectedProductLists) ? "✓ Ready" : "✗ Required"}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2.5">
                <button
                  onClick={handleProcessOrder}
                  disabled={!canBeProcessed || submitting}
                  className={`w-full py-2.5 rounded-lg text-xs font-bold text-white shadow transition-all border-none flex items-center justify-center gap-1.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:shadow-lg ${(!canBeProcessed || submitting) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                    }`}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={13} className="animate-spin" /> Dispatching...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={13} /> Dispatch & Assign Order
                    </>
                  )}
                </button>

                <button
                  onClick={() => handleDispatchProducts(false)}
                  disabled={submitting}
                  className="w-full py-2.5 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all cursor-pointer disabled:opacity-40 flex items-center justify-center gap-1.5"
                >
                  Cancel Dispatch
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* RIDER SELECT MODAL */}
      {showRiderModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center animate-fadeIn" onClick={() => setShowRiderModal(false)}>
          <div className="bg-white rounded-2xl max-w-[480px] w-[95%] max-h-[80vh] flex flex-col shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-bold text-slate-800">Select Available Rider</h3>
              <button
                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
                onClick={() => setShowRiderModal(false)}
              >
                <X size={16} />
              </button>
            </div>

            {/* Rider Search */}
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search available riders by name..."
                  value={riderSearch}
                  onChange={(e) => setRiderSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Riders List */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
              {filteredDrivers.length === 0 ? (
                <div className="text-center py-6 text-xs text-slate-400 italic">No riders found.</div>
              ) : (
                filteredDrivers.map((driver) => {
                  const isCurrent = driver.id === selectedDriver;
                  return (
                    <div
                      key={driver.id}
                      onClick={() => {
                        onDriverSelect(driver);
                        setShowRiderModal(false);
                        toast.success(`Rider ${driver.name} assigned!`);
                      }}
                      className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer group ${isCurrent
                        ? "border-indigo-500 bg-indigo-50/40"
                        : "border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/10"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={driver.profilePicture || "https://via.placeholder.com/40"}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover border border-slate-200 bg-white"
                        />
                        <div>
                          <div className="text-xs font-bold text-slate-800">{driver.name}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">📞 {driver.mobileNumber}</div>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-[10px] font-bold text-slate-500 border border-slate-200/50">
                        {driver.pendingOrdersCount} ongoing
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* ADD MEDICINE SPLIT MODAL */}
      {showProductModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center animate-fadeIn" onClick={() => setShowProductModal(false)}>
          <div className="bg-white rounded-2xl max-w-[95vw] w-[1140px] max-h-[90vh] flex flex-col shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Add Medicines from Clinic Stock</h3>
                <p className="text-[0.65rem] text-slate-400 mt-0.5">Select identical medicines and batch details matching patient requirements</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search stock by product name..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-xs w-[240px] outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <button
                  className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
                  onClick={() => setShowProductModal(false)}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Split Screen Modal Body */}
            <div className="flex-1 flex overflow-hidden bg-slate-50/50">

              {/* Left Column - Patient Requirements */}
              <div className="w-[300px] flex-shrink-0 bg-white border-r border-slate-200 overflow-y-auto p-5 space-y-6">

                {/* Ordered Medicines list */}
                <div className="space-y-3">
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Ordered Products</h4>
                  <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                    {orderDetails?.products?.map((p: OrderProduct) => {
                      const effective = getEffectiveProduct(p);
                      const matchedQty = getMatchedQty(effective.productId.toString());
                      const isComplete = matchedQty === effective.quantity;

                      return (
                        <div key={p.productItemId} className={`p-2.5 border rounded-lg text-xs font-semibold flex flex-col gap-1 transition-all ${isComplete ? "bg-emerald-50/50 border-emerald-200" : "bg-slate-55 bg-slate-50 border-slate-200"
                          }`}>
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-slate-700 truncate pr-2" title={effective.name}>{effective.name}</span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap flex-shrink-0 ${isComplete ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"
                              }`}>
                              Qty: {effective.quantity}
                            </span>
                          </div>
                          {effective.isSubstituted && (
                            <div className="text-[9px] text-indigo-500 font-semibold">
                              Substituted from: {effective.originalName}
                            </div>
                          )}
                          {matchedQty > 0 && (
                            <div className="text-[10px] text-slate-500 font-medium">
                              Dispatched Qty: <span className="font-extrabold text-slate-700">{matchedQty}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>

              {/* Right Column - Available Stock Search Grid */}
              <div className="flex-1 flex flex-col overflow-hidden p-6 bg-white">
                <div className="grid grid-cols-[2fr_1fr_1fr_0.8fr_1fr_0.6fr_0.8fr] gap-2 px-4 py-2 bg-slate-50 rounded-lg text-[10px] font-bold text-slate-400 uppercase mb-2">
                  <div>Name</div>
                  <div>Batch</div>
                  <div>Expiry</div>
                  <div>Qty/Pack</div>
                  <div>MRP</div>
                  <div className="text-center">Stocks</div>
                  <div className="text-center">Action</div>
                </div>

                <div className="flex-1 overflow-y-auto min-h-[300px] pr-1">
                  {modalLoading ? (
                    <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-2">
                      <Loader2 size={24} className="animate-spin text-indigo-500" />
                      <span className="text-xs">Searching inventory stocks...</span>
                    </div>
                  ) : availableProducts.length === 0 ? (
                    <div className="text-center py-16 text-slate-400 text-xs italic">
                      No matching products found in clinic stock.
                    </div>
                  ) : (
                    availableProducts.map((product) => {
                      const isAdded = selectedProductLists.some(p => p.id === product.id || p.product_id === product.id);
                      return (
                        <div key={product.id} className={`grid grid-cols-[2fr_1fr_1fr_0.8fr_1fr_0.6fr_0.8fr] gap-2 items-center px-4 py-2.5 border-b border-slate-100 transition-colors text-xs font-semibold ${isAdded ? "bg-emerald-50/50 text-emerald-800" : "hover:bg-slate-50/50 text-slate-700"}`}>
                          <div className="flex items-center gap-3 pr-2 min-w-0">
                            <img src={product.product_image || defaultProduct} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0 border border-slate-100" />
                            <div className="min-w-0">
                              <div className="text-[11px] font-bold text-slate-800 truncate" title={product.product_name}>{product.product_name}</div>
                              <div className="text-[9px] text-slate-400 truncate">{product.manufacturer_name}</div>
                            </div>
                          </div>
                          <div className="truncate text-slate-600">{product.batch_number || "-"}</div>
                          <div className="text-slate-500">
                            {product.expiry_date ? new Date(product.expiry_date).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "-"}
                          </div>
                          <div className="text-slate-500">{product.noOfItemsInPack}</div>
                          <div className="text-slate-600 font-bold">₹ {product.mrp?.toFixed(2)}</div>
                          <div className="text-center text-slate-600">{product.remainingStocks}</div>
                          <div className="text-center">
                            {isAdded ? (
                              <button
                                onClick={() => handleRemoveProduct(product.id)}
                                className="group px-2.5 py-1.5 rounded-lg text-[10px] font-extrabold border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-all cursor-pointer w-full text-center"
                              >
                                <span className="group-hover:hidden">Added</span>
                                <span className="hidden group-hover:inline">Remove</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAddProduct(product)}
                                className="px-2.5 py-1.5 rounded-lg text-[10px] font-extrabold border-none cursor-pointer bg-indigo-500 hover:bg-indigo-600 text-white transition-colors w-full text-center"
                              >
                                Add
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end bg-slate-50/30">
              <button
                onClick={() => setShowProductModal(false)}
                className="px-5 py-2.5 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white border-none cursor-pointer shadow-sm transition-all"
              >
                Done
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default DispatchProducts;
